/**
 * @module components/analysis/GenreFitRadar
 * @description
 *   Recharts radar: song polygon vs selected-genre center polygon.
 *
 *   Skim notes:
 *   - Recharts stays isolated in this file (parent only passes axes).
 *   - focusAxisId enlarges that axis label and draws compact focus dots.
 *   - Desktop: hovering a vertex shows a song−genre % badge as an HTML portal.
 *   - Compact (max-lg): tap a vertex to pin the badge; tap again or outside clears.
 *     No mouseenter open (avoids sticky hover after tap).
 *   - Chart is nudged right (cx + left margin) so west labels like
 *     "Rhythm density" are not clipped by the card edge.
 */

import React, { useMemo, useState, useCallback, useLayoutEffect, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import useMediaQuery, { COMPACT_MAX_LG } from '../../hooks/useMediaQuery';
/**
 * Map trendFitBreakdown.axes → Recharts radar rows.
 * @param {Array<{ id?: string, label?: string, song?: number, genreCenter?: number }>|null|undefined} axes
 * @returns {Array<{ axisId: string|null, axis: string, song: number, genre: number, deltaPct: number }>}
 */
export function mapAxesToRadarData(axes) {
  if (!Array.isArray(axes) || axes.length === 0) return [];
  return axes.map((axis) => {
    const song = clamp01(axis.song);
    const genre = clamp01(axis.genreCenter);
    return {
      axisId: axis.id || null,
      axis: axis.label || axis.id || 'Axis',
      song,
      genre,
      deltaPct: Math.round((song - genre) * 100),
    };
  });
}

/** Format signed song−genre gap for the focus badge (e.g. +9%, 0%, −5%). */
export function formatAxisDeltaPct(deltaPct) {
  const n = Number(deltaPct);
  if (!Number.isFinite(n)) return '0%';
  if (n > 0) return `+${n}%`;
  return `${n}%`;
}

function clamp01(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

/** Compact focus / hover markers — thin stroke so song/genre dots do not merge. */
const FOCUS_DOT_R = 3.25;
const HOVER_DOT_R = 5;
const FOCUS_DOT_STROKE = 1;
/**
 * Invisible hit target so small dots are easy to hover.
 * Same radius gates the % badge and the visible vertex marker.
 */
const FOCUS_HIT_R = 28;

/**
 * Screen coords for the vertex center (for the delta badge portal).
 * @param {SVGElement} target
 * @param {number} cx
 * @param {number} cy
 * @returns {{ x: number, y: number }|null}
 */
function vertexScreenPoint(target, cx, cy) {
  const svg = target.ownerSVGElement;
  if (!svg) return null;
  const pt = svg.createSVGPoint();
  pt.x = cx;
  pt.y = cy;
  const ctm = svg.getScreenCTM();
  if (!ctm) return null;
  const screen = pt.matrixTransform(ctm);
  return { x: screen.x, y: screen.y };
}

/**
 * @param {object} props
 * @param {number} props.cx
 * @param {number} props.cy
 * @param {string} props.fill
 * @param {string|null} props.axisId
 * @param {(info: { axisId: string, x: number, y: number }|null) => void} props.onVertexHover
 * @param {boolean} [props.isCompact=false] tap-pin instead of hover
 * @param {string|null} [props.pinnedAxisId] currently pinned axis (compact)
 * @param {boolean} [props.visible=true] draw the filled marker (false = hit-target only)
 * @param {boolean} [props.emphasized=false] slightly larger marker while hovered/pinned
 */
function HoverVertex({
  cx,
  cy,
  fill,
  axisId,
  onVertexHover,
  isCompact = false,
  pinnedAxisId = null,
  visible = true,
  emphasized = false,
}) {
  if (cx == null || cy == null || !axisId) return null;

  const enter = (event) => {
    if (isCompact) return;
    const screen = vertexScreenPoint(event.currentTarget, cx, cy);
    if (!screen) return;
    onVertexHover({ axisId, x: screen.x, y: screen.y });
  };

  const leave = () => {
    if (isCompact) return;
    onVertexHover(null);
  };

  const onClick = (event) => {
    if (!isCompact) return;
    event.stopPropagation();
    const screen = vertexScreenPoint(event.currentTarget, cx, cy);
    if (!screen) return;
    // Tap same vertex again clears; otherwise pin this spoke.
    if (pinnedAxisId === axisId) {
      onVertexHover(null);
      return;
    }
    onVertexHover({ axisId, x: screen.x, y: screen.y });
  };

  return (
    <g
      data-vertex-hit={axisId}
      onMouseEnter={enter}
      onMouseLeave={leave}
      onClick={onClick}
      style={{ cursor: isCompact ? 'pointer' : 'default' }}
    >
      {/* Larger invisible hit area — same radius as % badge affordance */}
      <circle cx={cx} cy={cy} r={FOCUS_HIT_R} fill="transparent" stroke="none" />
      {visible ? (
        <circle
          cx={cx}
          cy={cy}
          r={emphasized ? HOVER_DOT_R : FOCUS_DOT_R}
          fill={fill}
          stroke="rgba(255,255,255,0.85)"
          strokeWidth={FOCUS_DOT_STROKE}
          style={{ pointerEvents: 'none' }}
        />
      ) : null}
    </g>
  );
}
/**
 * HTML badge portal — sits outside the vertex, pushed away from chart center.
 */
function DeltaBadgePortal({ hover, row, containerRect }) {
  const tipRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!hover || !row) return undefined;

    const place = () => {
      const tip = tipRef.current;
      const tipW = tip?.offsetWidth || 56;
      const tipH = tip?.offsetHeight || 28;
      // Push badge outward from the plot center so it clears polygons + spoke.
      const centerX = containerRect
        ? containerRect.left + containerRect.width * 0.54
        : hover.x;
      const centerY = containerRect
        ? containerRect.top + containerRect.height * 0.48
        : hover.y;
      const dx = hover.x - centerX;
      const dy = hover.y - centerY;
      const len = Math.hypot(dx, dy) || 1;
      // Clear the vertex, then clear typical axis-label ring (~18–28px).
      const push = 36;
      let left = hover.x + (dx / len) * push - tipW / 2;
      let top = hover.y + (dy / len) * push - tipH / 2;

      left = Math.min(left, window.innerWidth - tipW - 8);
      left = Math.max(8, left);
      top = Math.min(top, window.innerHeight - tipH - 8);
      top = Math.max(8, top);
      setCoords({ top, left });
    };

    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [hover, row, containerRect]);

  if (!hover || !row) return null;

  const label = formatAxisDeltaPct(row.deltaPct);
  const delta = row.deltaPct;
  const glyph = delta > 0 ? '▲' : delta < 0 ? '▼' : '●';

  return createPortal(
    <div
      ref={tipRef}
      role="tooltip"
      data-axis-delta={row.axisId || undefined}
      style={{ top: coords.top, left: coords.left }}
      className="fixed z-[110] pointer-events-none rounded-lg border border-white/[0.16] bg-critic-bg-deep/95 backdrop-blur-xl px-2.5 py-1.5 shadow-lg"
    >
      <p className="text-[11px] font-semibold tabular-nums text-critic-text-primary leading-none whitespace-nowrap">
        <span className="mr-1 text-critic-text-muted" aria-hidden>
          {glyph}
        </span>
        {label}
      </p>
    </div>,
    document.body
  );
}

/**
 * @param {object} props
 * @param {Array} [props.axes]
 * @param {string} [props.genreLabel='Genre']
 * @param {string|null} [props.focusAxisId]
 * @param {string} [props.className]
 */
export default function GenreFitRadar({
  axes,
  genreLabel = 'Genre',
  focusAxisId = null,
  className = '',
}) {
  const containerRef = useRef(null);
  const [hover, setHover] = useState(null); // { axisId, x, y } | null
  const [containerRect, setContainerRect] = useState(null);
  const isCompact = useMediaQuery(COMPACT_MAX_LG);

  const data = mapAxesToRadarData(axes);

  const onVertexHover = useCallback((info) => {
    if (info && containerRef.current) {
      setContainerRect(containerRef.current.getBoundingClientRect());
    }
    setHover(info);
  }, []);

  // Compact: pointerdown outside the radar clears a pinned delta badge.
  useEffect(() => {
    if (!isCompact || !hover) return undefined;

    const onPointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (containerRef.current?.contains(target)) return;
      setHover(null);
    };

    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [isCompact, hover]);

  // Leaving compact clears a pinned badge so desktop hover can take over cleanly.
  useEffect(() => {
    if (!isCompact) setHover(null);
  }, [isCompact]);

  // Always mount hit-target dots on every spoke (same radius as % badge).
  // Visible marker when tip-focused OR when this spoke is hovered/pinned.
  const genreDot = useMemo(
    () => (props) => {
      const { cx, cy, payload } = props;
      const focused = Boolean(focusAxisId) && payload?.axisId === focusAxisId;
      const hovered = Boolean(hover?.axisId) && payload?.axisId === hover.axisId;
      return (
        <HoverVertex
          cx={cx}
          cy={cy}
          fill="rgba(160, 160, 168, 0.95)"
          axisId={payload?.axisId}
          onVertexHover={onVertexHover}
          isCompact={isCompact}
          pinnedAxisId={hover?.axisId || null}
          visible={focused || hovered}
          emphasized={hovered}
        />
      );
    },
    [focusAxisId, hover, onVertexHover, isCompact]
  );

  const songDot = useMemo(
    () => (props) => {
      const { cx, cy, payload } = props;
      const focused = Boolean(focusAxisId) && payload?.axisId === focusAxisId;
      const hovered = Boolean(hover?.axisId) && payload?.axisId === hover.axisId;
      return (
        <HoverVertex
          cx={cx}
          cy={cy}
          fill="#007AFF"
          axisId={payload?.axisId}
          onVertexHover={onVertexHover}
          isCompact={isCompact}
          pinnedAxisId={hover?.axisId || null}
          visible={focused || hovered}
          emphasized={hovered}
        />
      );
    },
    [focusAxisId, hover, onVertexHover, isCompact]
  );
  if (data.length === 0) {
    return (
      <p className="text-sm text-critic-text-muted" role="status">
        Genre fit chart unavailable for this analysis.
      </p>
    );
  }

  const focusRow = focusAxisId ? data.find((d) => d.axisId === focusAxisId) : null;
  const focusLabel = focusRow?.axis || null;
  const focusDeltaLabel = focusRow ? formatAxisDeltaPct(focusRow.deltaPct) : null;
  const hoverRow = hover ? data.find((d) => d.axisId === hover.axisId) : null;

  return (
    <div
      ref={containerRef}
      className={[
        'genre-fit-radar w-full outline-none relative',
        // Strip Recharts default focus outlines (card is not keyboard-nav target).
        '[&_.recharts-wrapper]:outline-none [&_.recharts-surface]:outline-none',
        '[&_svg]:outline-none [&_*:focus]:outline-none [&_*:focus-visible]:outline-none',
        className || 'h-64 sm:h-72',
      ]
        .join(' ')
        .trim()}
      role="img"
      aria-label={
        focusLabel
          ? `Genre fit radar comparing your track to ${genreLabel}, focused on ${focusLabel} (${focusDeltaLabel})`
          : `Genre fit radar comparing your track to ${genreLabel}`
      }
      data-focus-axis={focusAxisId || undefined}
      data-focus-delta={focusDeltaLabel || undefined}
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart
          cx="54%"
          cy="48%"
          outerRadius="72%"
          margin={{ top: 12, right: 12, bottom: 8, left: 40 }}
          data={data}
          style={{ outline: 'none' }}
          tabIndex={-1}
        >
          <PolarGrid stroke="rgba(255,255,255,0.12)" />
          {/* Axis labels: bump weight/size when this spoke is tip-focused */}
          <PolarAngleAxis
            dataKey="axis"
            tick={(tickProps) => {
              const { x, y, payload, textAnchor } = tickProps;
              const focused =
                Boolean(focusAxisId) &&
                data.some(
                  (d) => d.axis === payload?.value && d.axisId === focusAxisId
                );
              return (
                <text
                  x={x}
                  y={y}
                  textAnchor={textAnchor}
                  fill={focused ? 'rgb(230, 230, 235)' : 'rgb(160, 160, 168)'}
                  fontSize={focused ? 12 : 11}
                  fontWeight={focused ? 600 : 400}
                >
                  {payload?.value}
                </text>
              );
            }}
          />
          <PolarRadiusAxis
            angle={30}
            domain={[0, 1]}
            tick={false}
            axisLine={false}
          />
          {/* Gray = genre pack centers; blue = this track (0–1 per axis) */}
          <Radar
            name={genreLabel}
            dataKey="genre"
            stroke="rgba(160, 160, 168, 0.85)"
            fill="rgba(160, 160, 168, 0.18)"
            fillOpacity={1}
            dot={genreDot}
            activeDot={false}
            isAnimationActive={false}
          />
          <Radar
            name="Your track"
            dataKey="song"
            stroke="#007AFF"
            fill="rgba(0, 122, 255, 0.35)"
            fillOpacity={1}
            dot={songDot}
            activeDot={false}
            isAnimationActive={false}
          />
          <Legend
            wrapperStyle={{
              color: 'rgb(200, 200, 208)',
              fontSize: 12,
              pointerEvents: 'none',
            }}
          />
        </RadarChart>
      </ResponsiveContainer>
      <DeltaBadgePortal hover={hover} row={hoverRow} containerRect={containerRect} />
    </div>
  );
}
